/**
 * Google Ads conversions. window.gtag is defined in index.html's <head> before anything else runs,
 * with the consent default the cookie bar's answer allows; a conversion sent before the tag has
 * loaded waits in dataLayer.
 */

export const ADS_ID = 'AW-17715866267';

// Conversion actions in the Google Ads account (Goals → Conversions)
export const ADS_LABELS = {
  purchase: 'Qt-sCJv5hcUbEJvVyv9B', // "Subscribe" (the old "Purchase" action is removed)
  signup: 'mlnSCIm7qcYbEJvVyv9B', // "Sign-up2"
  videoMade: 'ySq5CL3wvIgdEJvVyv9B', // "Video made" (goal: Submit lead form)
  checkoutStarted: 'ohcbCObzuKcdEJvVyv9B', // "Checkout started" (goal: Begin checkout, count One)
};

// The second account (2026-11-05, configured in index.html). Every conversion goes to each account that
// has a label for it
export const ADS_ID_2 = 'AW-18557780691';
export const ADS_LABELS_2 = {
  purchase: 'i__kCOT-0LIdENOFhZFF', // "Purchase" (goal: Purchase, count Every, primary)
  signup: 'JErQCOnp0rIdENOFhZFF', // "Sign-up" (goal: Sign-up, count One)
  videoMade: 'lNApCOzp0rIdENOFhZFF', // "Video made" (goal: Submit lead form, count One)
  checkoutStarted: 'aiVyCO_p0rIdENOFhZFF', // "Checkout started" (goal: Begin checkout, count One)
};

const ACCOUNTS = [
  { id: ADS_ID, labels: ADS_LABELS },
  { id: ADS_ID_2, labels: ADS_LABELS_2 },
];

// A checkout started is worth this share of what it would pay: enough for Google to learn who comes
// close to buying, small enough that a real purchase (its full price) always counts for more
export const CHECKOUT_SHARE = 0.1;

// A new account is one made in the last day; an old one signing in on a new device is not a sign-up
const NEW_ACCOUNT_MS = 24 * 60 * 60 * 1000;
// A visitor's clock a little ahead of the server's is not a video from the future
const CLOCK_SKEW_MS = 5 * 60 * 1000;

/**
 * Milliseconds since `timestamp`. The API sends UTC with no zone ("2026-09-28T19:30:00",
 * datetime.utcnow), which a browser reads as ITS local time: in Mexico or Colombia a video
 * made a minute ago read as five or six hours ahead, so no sign-up or video was ever
 * reported from the Americas (0 "Video made" from 17 ad sign-ups who all made one, 09-28).
 */
export const ageOf = (timestamp, now = Date.now()) => {
  if (!timestamp) return NaN;
  const text = String(timestamp);
  const zoned = /(Z|[+-]\d{2}:?\d{2})$/.test(text) ? text : `${text}Z`;
  return now - new Date(zoned).getTime();
};

const isRecent = (timestamp, within, now) => {
  const age = ageOf(timestamp, now);
  return age >= -CLOCK_SKEW_MS && age < within;
};

const send = (...args) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return false;
  try {
    window.gtag(...args);
    return true;
  } catch (error) {
    console.error('Google Ads:', error);
    return false;
  }
};

/** One conversion event per account that has an action for `kind`; true when any went out. */
const convert = (kind, params) => ACCOUNTS
  .filter(({ labels }) => labels[kind])
  .map(({ id, labels }) => send('event', 'conversion', { send_to: `${id}/${labels[kind]}`, ...params }))
  .some(Boolean);

const once = (key) => {
  try {
    if (window.localStorage.getItem(key)) return false;
    window.localStorage.setItem(key, '1');
  } catch {
    // no storage: Google's own transaction_id still keeps one per id
  }
  return true;
};

/** The signed-in person's email, from Clerk; '' when unknown. */
export const adsEmail = () => {
  try {
    return window.Clerk?.user?.primaryEmailAddress?.emailAddress || '';
  } catch {
    return '';
  }
};

/**
 * Enhanced conversions: the buyer's email goes with a conversion, so Google can match a purchase made
 * on a computer to an ad clicked on a phone (our payers mostly pay on a computer). gtag hashes it
 * (SHA-256) before it leaves the browser, and Consent Mode sends it only with ad_user_data granted.
 */
export const setAdsUserData = (email = adsEmail()) => {
  const clean = String(email || '').trim().toLowerCase();
  return clean.includes('@') ? send('set', 'user_data', { email: clean }) : false;
};

/** Checkout started: the person left for Stripe's checkout (utils/checkoutTrip.js, every checkout). */
export const trackCheckoutStartedConversion = ({ value, currency = 'USD' } = {}) => {
  setAdsUserData();
  return convert('checkoutStarted', {
    value: Math.max(0.01, Math.round((Number(value) || 1) * CHECKOUT_SHARE * 100) / 100),
    currency: String(currency || 'USD').toUpperCase(),
  });
};

export const trackPurchaseConversion = ({ value, transactionId, currency = 'USD' }) => {
  setAdsUserData();
  return convert('purchase', {
    value: value || 1.0,
    currency,
    transaction_id: transactionId,
  });
};

/**
 * A purchase, once per checkout session in this browser: a pack's or a Clip pack's return page, and a payment
 * the server credited with no page open (GET /subscription/waiting `unreported`), may each see the same
 * session. Google's transaction_id is the last guard.
 */
export const trackPurchaseOnce = ({ transactionId, ...rest }) => {
  if (transactionId && !once(`ads.purchase.${transactionId}`)) return false;
  return trackPurchaseConversion({ transactionId, ...rest });
};

/** Sign-up2: once per account, and only for an account made in the last day. */
export const trackSignupConversion = (user, now = Date.now()) => {
  if (!user?.id || !user.created_at) return false;
  if (!isRecent(user.created_at, NEW_ACCOUNT_MS, now)) return false;
  if (!once(`ads.signup.${user.id}`)) return false;
  return convert('signup', {
    value: 0.1,
    currency: 'USD',
    transaction_id: `signup-${user.id}`,
  });
};

export const trackSubscribeConversion = ({ value, currency = 'USD' }) =>
  send('event', 'subscribe', { send_to: ACCOUNTS.map(({ id }) => id), value: value || 1.0, currency });

// A video counts while it is new: opening an old one after an ad click is not making one
const NEW_VIDEO_MS = 2 * 24 * 60 * 60 * 1000;

/**
 * Video made: the owner's AI Agent video finished. Once per video, though the page opens it many
 * times, and never for an admin looking at someone else's.
 */
export const trackVideoMadeConversion = (project, user, now = Date.now()) => {
  if (!ACCOUNTS.some(({ labels }) => labels.videoMade) || !project?.id || project.status !== 'completed') return false;
  if (!user || user.is_admin) return false;
  if (!isRecent(project.created_at, NEW_VIDEO_MS, now)) return false;
  const projectId = project.id;
  if (!once(`ads.videoMade.${projectId}`)) return false;
  return convert('videoMade', {
    value: 0.5,
    currency: 'USD',
    transaction_id: `video-${projectId}`,
  });
};

/** The cookie bar's answer, for Google's Consent Mode */
export const updateAdsConsent = (granted) => {
  const state = granted ? 'granted' : 'denied';
  return send('consent', 'update', {
    ad_storage: state,
    ad_user_data: state,
    // No remarketing: the privacy policy promises no personalised ads elsewhere
    ad_personalization: 'denied',
    analytics_storage: state,
  });
};
